import { motion, AnimatePresence, useScroll, useTransform, useSpring, MotionValue } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import {
  ArrowRight, CheckCircle2, Lock, Zap, X,
} from "lucide-react";
import {
  PaymentsIcon, ProcurementIcon, CommerceIcon, HealthIcon,
  NetworkIcon, AcademicIcon, DocumentIcon, IdentityIcon,
  BuildingIcon as BoardingIcon, UsersIcon, ShieldCheckIcon,
  InsightsIcon, StoreIcon, PackageIcon, PowerIcon,
  ComplianceIcon, ActivityIcon, LayersIcon,
  TrendingUpIcon, ScrollTextIcon, RocketIcon, GraduationCapIcon,
  QrCodeIcon, BanknoteIcon, SparklesIcon, GlobeIcon,
} from "@/components/icons/fundred-icons";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const INK = "#082F49";
const OCEAN = "#0C4A6E";
const SKY = "#0EA5E9";
const CYAN = "#06B6D4";
const TEAL = "#14B8A6";
const MIST = "#E0F2FE";
const ICE = "#F0F9FF";
const LINE = "#BAE6FD";
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

// ════════════════════════════════════════════════════════════════
// DATA
// ════════════════════════════════════════════════════════════════

const LOOP_STEPS = [
  { step: "Connect", icon: NetworkIcon, color: SKY, desc: "Managed internet, local edge, power backup" },
  { step: "Identify", icon: IdentityIcon, color: CYAN, desc: "Tokenised NFC card or QR credential" },
  { step: "Serve", icon: HealthIcon, color: TEAL, desc: "Cashless canteen tap with meal rules" },
  { step: "Record", icon: ActivityIcon, color: SKY, desc: "Consumption logged, stock auto-reduced" },
  { step: "Forecast", icon: InsightsIcon, color: CYAN, desc: "Demand predicted from real data" },
  { step: "Procure", icon: ProcurementIcon, color: OCEAN, desc: "Transparent supplier comparison" },
  { step: "Verify", icon: ShieldCheckIcon, color: TEAL, desc: "Delivery matched to purchase order" },
  { step: "Settle", icon: BanknoteIcon, color: OCEAN, desc: "Payment instruction to licensed partner" },
  { step: "Reconcile", icon: ComplianceIcon, color: SKY, desc: "Automatic fund matching" },
  { step: "Report", icon: ScrollTextIcon, color: CYAN, desc: "Outcome evidence for every stakeholder" },
];

const RAILS = [
  {
    name: "LearnerPass",
    icon: QrCodeIcon,
    color: SKY,
    tagline: "Cashless learner identity",
    desc: "Tokenised NFC cards and QR fallback give every learner meal entitlement, parent-funded top-ups, spending limits, approved-item restrictions, and lost-card freeze — all offline-capable.",
    features: [
      "Meal entitlement and subsidy rules",
      "Parent-funded top-ups via mobile money",
      "Daily and weekly spending limits",
      "Approved-item restrictions per learner",
      "Lost-card freeze and replacement",
      "Offline tap allowance with sync",
      "QR and USSD fallback",
      "Parent receipt by SMS or WhatsApp",
    ],
  },
  {
    name: "School Commerce",
    icon: StoreIcon,
    color: CYAN,
    tagline: "Controlled marketplace",
    desc: "A school-controlled marketplace for approved categories — food, uniforms, textbooks, transport, and connectivity. Parents see approved purchases; schools get controlled supplier comparisons.",
    features: [
      "School-approved supplier categories",
      "Food and feeding supplies",
      "Uniforms, textbooks and stationery",
      "Transport and fuel procurement",
      "Parent-visible approved purchases",
      "Supplier demand forecasting",
      "No unrestricted access to learner data",
      "One ledger, zero leakage",
    ],
  },
  {
    name: "School Connectivity",
    icon: NetworkIcon,
    color: TEAL,
    tagline: "Connectivity-as-a-Service",
    desc: "Not just internet — a managed operating layer. Fibre, mobile, fixed-wireless, or LEO satellite. Managed router, Wi-Fi zones, solar backup, local content cache, offline sync, and a monthly uptime report.",
    features: [
      "Connectivity assessment and site survey",
      "Fibre, mobile, fixed-wireless, or LEO satellite",
      "Solar and battery backup",
      "Managed router and firewall",
      "School-wide Wi-Fi with VLAN separation",
      "Local content cache and update queue",
      "Bandwidth quotas and application priority",
      "Monthly connectivity scorecard",
    ],
  },
  {
    name: "Supplier Finance",
    icon: BanknoteIcon,
    color: OCEAN,
    tagline: "Non-custodial payment control",
    desc: "Verified supplier onboarding, PO-to-delivery-to-invoice matching, school approval matrix, payment instruction to a licensed partner, reconciliation, and supplier performance — all auditable.",
    features: [
      "Verified supplier onboarding",
      "Three-way match: PO, delivery, invoice",
      "School approval matrix",
      "Payment instruction to licensed partner",
      "Automatic reconciliation",
      "Supplier delivery and price performance",
      "Procurement audit packs",
      "Non-custodial — no BRECCS wallet",
    ],
  },
];

const KIT_COMPONENTS = [
  { name: "Local edge appliance", icon: PackageIcon, desc: "Local BRECCS services, cache, offline queue" },
  { name: "Managed router/firewall", icon: NetworkIcon, desc: "VLAN separation, traffic shaping, LTE failover" },
  { name: "Wi-Fi access points", icon: GlobeIcon, desc: "Canteen, admin, classroom, staff coverage" },
  { name: "NFC/QR canteen terminal", icon: QrCodeIcon, desc: "Learner tap, offline queue, receipt printing" },
  { name: "Learner NFC cards", icon: IdentityIcon, desc: "Tokenised cashless meal entitlement" },
  { name: "Solar/battery backup", icon: PowerIcon, desc: "Power resilience for full-day operation" },
  { name: "Rugged admin tablet", icon: DocumentIcon, desc: "Receiving, stock, approvals, support" },
  { name: "Lockable network cabinet", icon: ShieldCheckIcon, desc: "Protects edge, router, switch, power" },
];

const DEVICE_TABS = [
  { id: "canteen", label: "Canteen Terminal", icon: QrCodeIcon, color: SKY },
  { id: "tablet", label: "Admin Tablet", icon: DocumentIcon, color: CYAN },
  { id: "connectivity", label: "Connectivity Dashboard", icon: NetworkIcon, color: TEAL },
  { id: "edge", label: "Edge Server", icon: PackageIcon, color: OCEAN },
] as const;

const LAUNCH_STEPS = [
  "Spreadsheet import and validation",
  "School structure templates",
  "Guardian bulk import",
  "Card printing and assignment",
  "Canteen catalogue setup",
  "Menu and subsidy configuration",
  "Staff role-specific training",
  "Payment-partner configuration",
  "First reconciliation test",
];

const ZIMBABWE_CARDS = [
  { title: "Multi-currency", desc: "USD and ZiG-aware invoices, receipts, and reporting.", icon: BanknoteIcon },
  { title: "Multi-rail payments", desc: "EcoCash, InnBucks, PayNow, ZIPIT, bank, cash — all reconciled.", icon: PaymentsIcon },
  { title: "Offline-resilient", desc: "Canteen, stock, procurement queue locally and sync later.", icon: PowerIcon },
  { title: "Boarding-native", desc: "Dorms, houses, roll calls, leave, welfare, and boarding fees.", icon: BoardingIcon },
  { title: "School-group ready", desc: "Group, school, campus, class, learner — control at every level.", icon: LayersIcon },
  { title: "Data protection", desc: "Built for Zimbabwe's Cyber and Data Protection Act.", icon: ShieldCheckIcon },
];

const BUYER_ROLES = [
  { role: "School owner", desc: "Own the school's digital infrastructure — connectivity, cashless operations, procurement, and financial records in one auditable system.", icon: ShieldCheckIcon },
  { role: "Headmaster", desc: "Keep the school running through outages. Know what needs attention today, not what happened last month.", icon: GraduationCapIcon },
  { role: "Bursar", desc: "Reconcile every payment automatically. Stop chasing unmatched deposits across currencies.", icon: BanknoteIcon },
  { role: "Procurement officer", desc: "Compare suppliers by total cost, reliability, and delivery risk. Catch split purchases and invoice duplication.", icon: TrendingUpIcon },
  { role: "Canteen operator", desc: "Control learner allowances, link every sale to inventory, and give parents top-up convenience.", icon: StoreIcon },
  { role: "Parent", desc: "See balances, pay fees, top up wallets, approve permissions, and get receipts — from your phone or USSD.", icon: UsersIcon },
];

const STATS = [
  { value: "10", label: "steps in the operational loop", icon: ActivityIcon },
  { value: "4", label: "integrated product rails", icon: LayersIcon },
  { value: "72h", label: "school launch standard", icon: RocketIcon },
  { value: "100%", label: "offline-capable core", icon: PowerIcon },
];

// ════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════
export function BreccsLanding() {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: ICE, color: INK, fontFamily: "'Inter', -apple-system, system-ui, sans-serif" }}>
      <HeroSection />
      <StatsBar />
      <OrbitalLoopSection />
      <RailsSection />
      <SchoolInABoxSection />
      <ZimbabweSection />
      <BuyerSection />
      <CTASection />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ANIMATED GRADIENT MESH BACKGROUND
// ════════════════════════════════════════════════════════════════
function GradientMesh({ variant }: { variant: "light" | "dark" }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute -top-32 -left-32 h-[600px] w-[600px] rounded-full opacity-30 blur-[100px]"
        style={{ background: `radial-gradient(circle, ${SKY}, transparent 70%)` }}
        animate={{ x: [0, 40, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 -right-32 h-[500px] w-[500px] rounded-full opacity-25 blur-[80px]"
        style={{ background: `radial-gradient(circle, ${CYAN}, transparent 70%)` }}
        animate={{ x: [0, -30, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-32 left-1/3 h-[400px] w-[400px] rounded-full opacity-20 blur-[80px]"
        style={{ background: `radial-gradient(circle, ${TEAL}, transparent 70%)` }}
        animate={{ x: [0, 50, 0], y: [0, -20, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />
      {variant === "dark" && (
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(${SKY}80 1px, transparent 1px), linear-gradient(90deg, ${SKY}80 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// HERO — kinetic typography, floating glass cards, scroll parallax
// ════════════════════════════════════════════════════════════════
function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);

  const words = ["Every", "learner", "served."];

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(180deg, ${ICE} 0%, ${ICE} 60%, white 100%)` }}>
      <GradientMesh variant="light" />

      <motion.div style={{ y, opacity, scale }} className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center pt-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mb-8 inline-flex items-center gap-2 rounded-full px-4 py-2 backdrop-blur-md"
          style={{ background: "rgba(224,242,254,0.6)", border: `1px solid ${LINE}` }}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: SKY }} />
            <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: OCEAN }} />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: OCEAN }}>Zimbabwe's School Commerce and Connectivity OS</span>
        </motion.div>

        {/* Kinetic headline */}
        <h1 className="text-5xl font-bold tracking-tight sm:text-7xl lg:text-[5.5rem]" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.0 }}>
          {words.map((word, i) => (
            <motion.span
              key={word}
              initial={{ opacity: 0, y: 60, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.8, ease: EASE }}
              className="inline-block"
              style={{ marginRight: i < words.length - 1 ? "0.25em" : 0 }}
            >
              {word}
            </motion.span>
          ))}
          <br />
          <motion.span
            initial={{ opacity: 0, y: 60, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 0.65, duration: 0.8, ease: EASE }}
            className="inline-block"
            style={{
              background: `linear-gradient(135deg, ${SKY}, ${CYAN}, ${TEAL})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Every payment controlled.
          </motion.span>
          <br />
          <motion.span
            initial={{ opacity: 0, y: 60, filter: "blur(10px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
            className="inline-block"
          >
            Every meal traceable.
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7, ease: EASE }}
          className="mx-auto mt-8 max-w-2xl text-base leading-relaxed sm:text-lg"
          style={{ color: "#0369A1", lineHeight: 1.65 }}
        >
          BRECCS is the closed operational loop that connects the learner, canteen, supply chain, payment control, and connectivity infrastructure into one measurable system — even when the internet or power fails.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6, ease: EASE }}
          className="mt-10 flex flex-col gap-3 sm:flex-row justify-center"
        >
          <a href="#audit" className="group inline-flex h-13 items-center justify-center gap-2 rounded-2xl px-7 py-3.5 text-sm font-bold text-white transition-all hover:scale-105"
            style={{ background: `linear-gradient(135deg, ${OCEAN}, ${SKY})`, boxShadow: `0 12px 32px -12px ${OCEAN}80` }}>
            Book a demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a href="#loop" className="inline-flex h-13 items-center justify-center gap-2 rounded-2xl px-7 py-3.5 text-sm font-bold backdrop-blur-md transition-all hover:scale-105"
            style={{ border: `1px solid ${LINE}`, background: "rgba(255,255,255,0.5)", color: OCEAN }}>
            Explore the platform
          </a>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-6"
        >
          {[
            { icon: Lock, label: "Data stays in Zimbabwe" },
            { icon: Zap, label: "Offline-resilient" },
            { icon: CheckCircle2, label: "Cyber & Data Protection Act ready" },
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-2">
              <b.icon className="h-4 w-4" style={{ color: OCEAN }} />
              <span className="text-xs" style={{ color: "#0369A1" }}>{b.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <div className="h-10 w-6 rounded-full border-2 flex items-start justify-center p-1.5" style={{ borderColor: LINE }}>
            <div className="h-2 w-1 rounded-full" style={{ background: OCEAN }} />
          </div>
          <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: "#0369A1" }}>Scroll</span>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// STATS BAR — glassmorphic floating bar
// ════════════════════════════════════════════════════════════════
function StatsBar() {
  return (
    <section className="relative -mt-16 z-20 px-4">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border backdrop-blur-xl lg:grid-cols-4"
          style={{
            borderColor: LINE,
            background: "rgba(255,255,255,0.7)",
            boxShadow: "0 20px 60px -20px rgba(12,74,110,0.25)",
          }}
        >
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex flex-col items-center gap-2 p-6 text-center" style={{ background: "rgba(255,255,255,0.5)" }}>
                <Icon size={24} style={{ color: SKY }} />
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                  className="text-3xl font-bold"
                  style={{ color: INK, letterSpacing: "-0.03em" }}
                >
                  {s.value}
                </motion.span>
                <span className="text-[11px] font-medium" style={{ color: "#0369A1" }}>{s.label}</span>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// ORBITAL LOOP — "The Living Circuit": SVG flowing particles, pulse
// rings, progress arc, and mission-control detail panel
// ════════════════════════════════════════════════════════════════
function OrbitalLoopSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoPlay) return;
    const t = setInterval(() => setActiveStep((i) => (i + 1) % LOOP_STEPS.length), 2800);
    return () => clearInterval(t);
  }, [autoPlay]);

  const active = LOOP_STEPS[activeStep];
  const ActiveIcon = active.icon;

  // SVG geometry
  const SIZE = 400;
  const C = SIZE / 2;
  const R = 150;

  const nodePositions = LOOP_STEPS.map((_, i) => {
    const a = (i / LOOP_STEPS.length) * Math.PI * 2 - Math.PI / 2;
    return { x: C + Math.cos(a) * R, y: C + Math.sin(a) * R };
  });

  const progressAngle = (activeStep + 1) / LOOP_STEPS.length;
  const arcEndA = progressAngle * Math.PI * 2 - Math.PI / 2;
  const arcEndX = C + Math.cos(arcEndA) * R;
  const arcEndY = C + Math.sin(arcEndA) * R;
  const largeArc = progressAngle > 0.5 ? 1 : 0;
  const progressPath = `M ${C} ${C - R} A ${R} ${R} 0 ${largeArc} 1 ${arcEndX} ${arcEndY}`;
  const fullCirclePath = `M ${C} ${C - R} A ${R} ${R} 0 0 1 ${C} ${C + R} A ${R} ${R} 0 0 1 ${C} ${C - R}`;

  return (
    <section id="loop" ref={sectionRef} className="relative py-24 sm:py-36 overflow-hidden" style={{ background: `linear-gradient(180deg, white 0%, ${ICE} 100%)` }}>
      <GradientMesh variant="light" />
      {/* Dot grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `radial-gradient(${INK} 1px, transparent 1px)`, backgroundSize: "24px 24px" }} />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
          className="mx-auto max-w-2xl text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(224,242,254,0.6)", border: `1px solid ${LINE}` }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: OCEAN }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: OCEAN }}>The operational loop</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: INK, letterSpacing: "-0.03em" }}>
            One integrated loop.<br />Not ten disconnected screens.
          </h2>
          <p className="mt-5 text-base leading-relaxed" style={{ color: "#0369A1" }}>
            Each step feeds the next. Data flows from the learner tap all the way to the board report — automatically, auditably, even offline.
          </p>
        </motion.div>

        <div className="mt-16 grid items-center gap-8 lg:grid-cols-[1fr_1fr]">
          {/* ── SVG Living Circuit ── */}
          <div className="relative mx-auto w-full max-w-[480px]">
            <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full h-full" style={{ overflow: "visible" }}>
              <defs>
                <linearGradient id="loopProgressGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={SKY} />
                  <stop offset="50%" stopColor={CYAN} />
                  <stop offset="100%" stopColor={TEAL} />
                </linearGradient>
                <linearGradient id="loopBaseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={LINE} stopOpacity="0.6" />
                  <stop offset="100%" stopColor={LINE} stopOpacity="0.2" />
                </linearGradient>
                <filter id="loopGlow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Outer decorative ring — slow rotation */}
              <g>
                <circle cx={C} cy={C} r={R + 42} fill="none" stroke={LINE} strokeWidth="1" strokeDasharray="2 10" opacity="0.35" />
                <animateTransform attributeName="transform" type="rotate" from={`0 ${C} ${C}`} to={`360 ${C} ${C}`} dur="80s" repeatCount="indefinite" />
              </g>

              {/* Middle decorative ring — counter rotation */}
              <g>
                <circle cx={C} cy={C} r={R + 22} fill="none" stroke={LINE} strokeWidth="1" strokeDasharray="1 5" opacity="0.25" />
                <animateTransform attributeName="transform" type="rotate" from={`360 ${C} ${C}`} to={`0 ${C} ${C}`} dur="60s" repeatCount="indefinite" />
              </g>

              {/* Tick marks around the ring */}
              {LOOP_STEPS.map((_, i) => {
                const a = (i / LOOP_STEPS.length) * Math.PI * 2 - Math.PI / 2;
                const x1 = C + Math.cos(a) * (R + 8);
                const y1 = C + Math.sin(a) * (R + 8);
                const x2 = C + Math.cos(a) * (R + 14);
                const y2 = C + Math.sin(a) * (R + 14);
                return <line key={`tick-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={LINE} strokeWidth="1.5" opacity="0.4" />;
              })}

              {/* Base ring */}
              <circle cx={C} cy={C} r={R} fill="none" stroke="url(#loopBaseGrad)" strokeWidth="2.5" />

              {/* Progress arc */}
              <path d={progressPath} fill="none" stroke="url(#loopProgressGrad)" strokeWidth="4" strokeLinecap="round" filter="url(#loopGlow)" />

              {/* Flowing data particles */}
              {Array.from({ length: 6 }).map((_, i) => (
                <circle key={`particle-${i}`} r="3" fill={SKY} opacity="0.5" filter="url(#loopGlow)">
                  <animateMotion dur={`${10}s`} repeatCount="indefinite" begin={`${i * 1.67}s}`} path={fullCirclePath} />
                </circle>
              ))}

              {/* Connection line: active node → center */}
              <line
                x1={nodePositions[activeStep].x}
                y1={nodePositions[activeStep].y}
                x2={C}
                y2={C}
                stroke={active.color}
                strokeWidth="2"
                strokeDasharray="5 5"
                opacity="0.35"
              >
                <animate attributeName="stroke-dashoffset" from="10" to="0" dur="0.6s" repeatCount="indefinite" />
              </line>

              {/* Center disc */}
              <circle cx={C} cy={C} r="52" fill={ICE} stroke={LINE} strokeWidth="1.5" opacity="0.7" />
              <circle cx={C} cy={C} r="46" fill="none" stroke={active.color} strokeWidth="1" opacity="0.2" />

              {/* Pulse rings from active node */}
              <circle cx={nodePositions[activeStep].x} cy={nodePositions[activeStep].y} r="22" fill="none" stroke={active.color} strokeWidth="2" opacity="0.6">
                <animate attributeName="r" from="22" to="55" dur="1.8s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.6" to="0" dur="1.8s" repeatCount="indefinite" />
              </circle>
              <circle cx={nodePositions[activeStep].x} cy={nodePositions[activeStep].y} r="22" fill="none" stroke={active.color} strokeWidth="1.5" opacity="0.4">
                <animate attributeName="r" from="22" to="55" dur="1.8s" begin="0.6s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.4" to="0" dur="1.8s" begin="0.6s" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Center icon — HTML overlay for crisp rendering */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <motion.div
                key={activeStep}
                initial={{ scale: 0.5, opacity: 0, rotateY: -90 }}
                animate={{ scale: 1, opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="grid h-20 w-20 place-items-center rounded-3xl text-white"
                style={{ background: `linear-gradient(135deg, ${active.color}, ${OCEAN})`, boxShadow: `0 12px 40px -10px ${active.color}80` }}
              >
                <ActiveIcon size={36} />
              </motion.div>
            </div>

            {/* Interactive node buttons — HTML overlay */}
            {nodePositions.map((pos, i) => {
              const step = LOOP_STEPS[i];
              const Icon = step.icon;
              const isActive = i === activeStep;
              const isCompleted = i < activeStep;
              const pctX = (pos.x / SIZE) * 100;
              const pctY = (pos.y / SIZE) * 100;
              return (
                <button
                  key={step.step}
                  onClick={() => { setActiveStep(i); setAutoPlay(false); }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${pctX}%`, top: `${pctY}%`, zIndex: isActive ? 20 : 5 }}
                >
                  <motion.div
                    animate={{ scale: isActive ? 1.15 : 1, y: isActive ? -3 : 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="grid h-11 w-11 place-items-center rounded-2xl border-2 backdrop-blur-md"
                    style={{
                      borderColor: isActive ? step.color : isCompleted ? `${step.color}50` : `${LINE}80`,
                      background: isActive ? `${step.color}25` : isCompleted ? `${step.color}08` : "rgba(255,255,255,0.85)",
                      boxShadow: isActive ? `0 8px 24px -4px ${step.color}60` : "0 2px 8px -4px rgba(12,74,110,0.15)",
                    }}
                  >
                    <Icon size={18} style={{ color: isActive ? step.color : isCompleted ? step.color : "#0369A1" }} />
                  </motion.div>
                  <span
                    className="absolute top-full mt-1.5 whitespace-nowrap text-[9px] font-bold uppercase tracking-wider transition-opacity"
                    style={{ color: isActive ? step.color : "#0369A1", opacity: isActive ? 1 : 0.45 }}
                  >
                    {step.step}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Mission Control Detail Panel ── */}
          <div className="space-y-5">
            {/* Active step card with 3D flip */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 30, rotateY: -12 }}
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                exit={{ opacity: 0, x: -30, rotateY: 12 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="relative overflow-hidden rounded-3xl border backdrop-blur-xl p-6"
                style={{
                  borderColor: `${active.color}30`,
                  background: `linear-gradient(135deg, ${active.color}08, rgba(255,255,255,0.7))`,
                  boxShadow: `0 20px 50px -20px ${active.color}30`,
                }}
              >
                {/* Giant step number watermark */}
                <span className="absolute -right-3 -top-6 text-[90px] font-black leading-none opacity-[0.05]" style={{ color: active.color }}>
                  {String(activeStep + 1).padStart(2, "0")}
                </span>

                <div className="relative flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="grid h-16 w-16 place-items-center rounded-2xl text-white shrink-0"
                    style={{ background: `linear-gradient(135deg, ${active.color}, ${OCEAN})`, boxShadow: `0 8px 24px -8px ${active.color}60` }}
                  >
                    <ActiveIcon size={32} />
                  </motion.div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#0369A1" }}>
                        Step {activeStep + 1} of {LOOP_STEPS.length}
                      </span>
                      <button
                        onClick={() => setAutoPlay(!autoPlay)}
                        className="flex items-center gap-1 text-[9px] font-medium transition-colors"
                        style={{ color: autoPlay ? active.color : "#94A3B8" }}
                      >
                        <motion.span
                          animate={{ opacity: autoPlay ? [1, 0.2, 1] : 0.2 }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: autoPlay ? active.color : "#94A3B8" }}
                        />
                        {autoPlay ? "Auto" : "Manual"}
                      </button>
                    </div>
                    <h3 className="text-2xl font-bold" style={{ color: INK }}>{active.step}</h3>
                  </div>
                </div>
                <p className="relative mt-4 text-base leading-relaxed" style={{ color: "#0369A1" }}>{active.desc}</p>
              </motion.div>
            </AnimatePresence>

            {/* Step progress rail */}
            <div className="rounded-2xl border p-4 backdrop-blur-md" style={{ borderColor: LINE, background: "rgba(255,255,255,0.6)" }}>
              <div className="flex items-center gap-1">
                {LOOP_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  const isActive = i === activeStep;
                  const isCompleted = i < activeStep;
                  return (
                    <button
                      key={step.step}
                      onClick={() => { setActiveStep(i); setAutoPlay(false); }}
                      className="group flex-1"
                      title={step.step}
                    >
                      {/* Progress bar segment */}
                      <div className="relative h-1 rounded-full overflow-hidden" style={{ background: `${LINE}60` }}>
                        <motion.div
                          initial={{ width: "0%" }}
                          animate={{ width: isActive || isCompleted ? "100%" : "0%" }}
                          transition={{ duration: 0.4, ease: EASE }}
                          className="h-full rounded-full"
                          style={{ background: `linear-gradient(90deg, ${step.color}, ${OCEAN})` }}
                        />
                      </div>
                      {/* Icon dot */}
                      <div className="mt-2 flex justify-center">
                        <motion.div
                          animate={{ scale: isActive ? 1.35 : 1 }}
                          transition={{ duration: 0.3, ease: EASE }}
                          className="grid h-7 w-7 place-items-center rounded-lg transition-colors"
                          style={{
                            background: isActive ? step.color : isCompleted ? `${step.color}15` : "transparent",
                            border: `1px solid ${isActive ? step.color : isCompleted ? `${step.color}40` : LINE}`,
                          }}
                        >
                          <Icon size={13} style={{ color: isActive ? "white" : isCompleted ? step.color : "#94A3B8" }} />
                        </motion.div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Loop summary */}
            <div className="rounded-2xl border p-5 backdrop-blur-md" style={{ borderColor: LINE, background: "rgba(255,255,255,0.6)" }}>
              <p className="text-sm font-medium leading-relaxed" style={{ color: INK }}>
                Connect the school. Identify the learner. Serve the meal. Record consumption. Forecast demand. Procure transparently. Verify delivery. Instruct settlement. Reconcile funds. Report the outcome.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// RAILS — glassmorphic cards with hover depth
// ════════════════════════════════════════════════════════════════
function RailsSection() {
  const [active, setActive] = useState(0);
  const rail = RAILS[active];
  const RailIcon = rail.icon;

  return (
    <section id="rails" className="relative py-24 sm:py-32" style={{ background: `linear-gradient(180deg, ${ICE} 0%, white 100%)` }}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(224,242,254,0.6)", border: `1px solid ${LINE}` }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: OCEAN }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: OCEAN }}>The BRECCS platform</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: INK, letterSpacing: "-0.03em" }}>Built on four rails.</h2>
          <p className="mt-5 text-base leading-relaxed" style={{ color: "#0369A1" }}>
            Not a feature list. An integrated operating loop where each rail feeds the next.
          </p>
        </div>

        {/* Rail selector */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RAILS.map((r, i) => {
            const Icon = r.icon;
            const isActive = i === active;
            return (
              <motion.button
                key={r.name}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActive(i)}
                className="group relative overflow-hidden rounded-3xl border p-5 text-left backdrop-blur-md transition-all"
                style={{
                  borderColor: isActive ? r.color : LINE,
                  background: isActive ? `${r.color}10` : "rgba(255,255,255,0.6)",
                  boxShadow: isActive ? `0 20px 40px -20px ${r.color}60` : "0 8px 20px -16px rgba(12,74,110,0.2)",
                }}
              >
                {isActive && (
                  <motion.div
                    layoutId={`rail-glow-${i}`}
                    className="absolute inset-0 opacity-10"
                    style={{ background: `radial-gradient(circle at 50% 0%, ${r.color}, transparent 70%)` }}
                  />
                )}
                <div className="relative">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl text-white transition-transform group-hover:scale-110" style={{ background: `linear-gradient(135deg, ${r.color}, ${OCEAN})` }}>
                    <Icon size={24} />
                  </span>
                  <p className="mt-4 text-sm font-bold" style={{ color: INK }}>{r.name}</p>
                  <p className="mt-0.5 text-[11px]" style={{ color: "#0369A1" }}>{r.tagline}</p>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Rail detail */}
        <AnimatePresence mode="wait">
          <motion.div
            key={rail.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="mt-8 overflow-hidden rounded-3xl border backdrop-blur-md"
            style={{ borderColor: `${rail.color}40`, background: "rgba(255,255,255,0.7)", boxShadow: `0 20px 50px -20px ${rail.color}40` }}
          >
            <div className="grid lg:grid-cols-[1fr_1.2fr]">
              <div className="p-6 sm:p-8" style={{ background: `${rail.color}08` }}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid h-14 w-14 place-items-center rounded-2xl text-white" style={{ background: `linear-gradient(135deg, ${rail.color}, ${OCEAN})` }}>
                    <RailIcon size={28} />
                  </span>
                  <div>
                    <p className="text-lg font-bold" style={{ color: INK }}>{rail.name}</p>
                    <p className="text-xs font-semibold" style={{ color: rail.color }}>{rail.tagline}</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#0369A1" }}>{rail.desc}</p>
              </div>
              <div className="grid grid-cols-1 gap-px sm:grid-cols-2" style={{ background: `${rail.color}10` }}>
                {rail.features.map((f, i) => (
                  <motion.div
                    key={f}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-2.5 bg-white/80 p-4 backdrop-blur-sm"
                  >
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ background: `${rail.color}15` }}>
                      <CheckCircle2 className="h-3.5 w-3.5" style={{ color: rail.color }} />
                    </span>
                    <span className="text-[13px] leading-snug" style={{ color: INK }}>{f}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// SCHOOL-IN-A-BOX — interactive device mockup showcase
// ════════════════════════════════════════════════════════════════
function SchoolInABoxSection() {
  const [activeDevice, setActiveDevice] = useState<typeof DEVICE_TABS[number]["id"]>("canteen");
  const activeTab = DEVICE_TABS.find((t) => t.id === activeDevice) ?? DEVICE_TABS[0];

  return (
    <section id="school-in-a-box" className="relative py-24 sm:py-36 overflow-hidden" style={{ background: `linear-gradient(180deg, ${INK} 0%, ${OCEAN} 100%)`, color: "white" }}>
      <GradientMesh variant="dark" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <PackageIcon size={14} style={{ color: SKY }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: SKY }}>School-in-a-Box</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ letterSpacing: "-0.03em" }}>
            One box. Every device ready.<br />Launch in 72 hours.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.65 }}>
            Edge server, router, Wi-Fi, NFC terminal, learner cards, rugged tablet, solar backup, and software — pre-configured and tested. The school operates for a full day without internet.
          </p>
        </div>

        {/* Device tab selector */}
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          {DEVICE_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeDevice;
            return (
              <motion.button
                key={tab.id}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveDevice(tab.id)}
                className="flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-bold backdrop-blur-md transition-all"
                style={{
                  borderColor: isActive ? tab.color : "rgba(255,255,255,0.12)",
                  background: isActive ? `${tab.color}20` : "rgba(255,255,255,0.04)",
                  color: isActive ? "white" : "rgba(255,255,255,0.6)",
                  boxShadow: isActive ? `0 8px 24px -8px ${tab.color}60` : "none",
                }}
              >
                <Icon size={18} style={{ color: isActive ? tab.color : "rgba(255,255,255,0.4)" }} />
                {tab.label}
              </motion.button>
            );
          })}
        </div>

        {/* Device mockup + info grid */}
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          {/* Mockup frame */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeDevice}
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -10 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="relative"
            >
              {activeDevice === "canteen" && <CanteenTerminalMockup />}
              {activeDevice === "tablet" && <AdminTabletMockup />}
              {activeDevice === "connectivity" && <ConnectivityDashboardMockup />}
              {activeDevice === "edge" && <EdgeServerMockup />}
            </motion.div>
          </AnimatePresence>

          {/* Side panel: kit components + launch timeline */}
          <div className="space-y-6">
            {/* Kit components */}
            <div className="rounded-3xl border p-5 backdrop-blur-md" style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }}>
              <p className="mb-4 text-sm font-bold">What's in the box</p>
              <div className="grid grid-cols-2 gap-3">
                {KIT_COMPONENTS.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05, duration: 0.3 }}
                      className="flex items-start gap-2.5"
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl" style={{ background: `${SKY}15`, border: `1px solid ${SKY}30` }}>
                        <Icon size={16} style={{ color: SKY }} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[12px] font-semibold leading-tight">{item.name}</p>
                        <p className="text-[10px] leading-tight" style={{ color: "rgba(255,255,255,0.45)" }}>{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* 72-hour launch */}
            <div className="rounded-3xl border p-5 backdrop-blur-md" style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)" }}>
              <div className="mb-4 flex items-center gap-2">
                <RocketIcon size={20} style={{ color: SKY }} />
                <p className="text-sm font-bold">72-hour school launch</p>
              </div>
              <div className="space-y-2.5">
                {LAUNCH_STEPS.map((step, i) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05, duration: 0.25 }}
                    className="flex items-center gap-3"
                  >
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-bold" style={{ background: `${SKY}25`, color: SKY, border: `1px solid ${SKY}40` }}>
                      {i + 1}
                    </span>
                    <span className="text-[12px]" style={{ color: "rgba(255,255,255,0.75)" }}>{step}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Canteen Terminal Mockup ─────────────────────────────────────
function CanteenTerminalMockup() {
  const [tapped, setTapped] = useState(false);
  return (
    <div className="mx-auto w-full max-w-sm">
      {/* Terminal frame */}
      <div className="overflow-hidden rounded-[2rem] border-2 p-1" style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)" }}>
        <div className="overflow-hidden rounded-[1.6rem]" style={{ background: "#0a0f1a" }}>
          {/* Status bar */}
          <div className="flex items-center justify-between px-4 py-2.5" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: "#10b981" }} />
              <span className="text-[10px] font-semibold" style={{ color: "rgba(255,255,255,0.6)" }}>Canteen Terminal — Online</span>
            </div>
            <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.4)" }}>12:42 PM</span>
          </div>

          {/* Learner card */}
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-2xl text-xl font-bold text-white" style={{ background: `linear-gradient(135deg, ${SKY}, ${OCEAN})` }}>
                TM
              </div>
              <div>
                <p className="text-sm font-bold text-white">Tatenda Moyo</p>
                <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }}>Form 2B · Card #4291</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[10px] uppercase" style={{ color: "rgba(255,255,255,0.4)" }}>Balance</p>
                <p className="text-lg font-bold" style={{ color: SKY }}>USD 4.50</p>
              </div>
            </div>

            {/* Meal rules */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <span className="text-[11px] font-medium" style={{ color: "#10b981" }}>Lunch — Approved</span>
                <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.4)" }}>USD 1.20</span>
              </div>
              <div className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}>
                <span className="text-[11px] font-medium" style={{ color: "#f59e0b" }}>Snacks — Daily limit reached</span>
                <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.4)" }}>Blocked</span>
              </div>
            </div>

            {/* Tap button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => { setTapped(true); setTimeout(() => setTapped(false), 1500); }}
              className="mt-4 w-full rounded-2xl py-4 text-sm font-bold text-white transition-all"
              style={{ background: tapped ? `linear-gradient(135deg, #10b981, ${SKY})` : `linear-gradient(135deg, ${SKY}, ${OCEAN})`, boxShadow: `0 8px 24px -8px ${SKY}80` }}
            >
              <AnimatePresence mode="wait">
                {tapped ? (
                  <motion.span key="served" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> Meal served — USD 1.20
                  </motion.span>
                ) : (
                  <motion.span key="tap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center justify-center gap-2">
                    <QrCodeIcon size={18} /> Tap learner card
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Recent transactions */}
            <div className="mt-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Today's transactions</p>
              <div className="space-y-1.5">
                {[
                  { name: "Lunch — Sadza & Stew", time: "12:38", amt: "1.20" },
                  { name: "Breakfast — Porridge", time: "07:15", amt: "0.50" },
                  { name: "Lunch — Sadza & Veggies", time: "Yesterday", amt: "1.00" },
                ].map((tx) => (
                  <div key={tx.name} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                    <div>
                      <p className="text-[11px] font-medium text-white">{tx.name}</p>
                      <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>{tx.time}</p>
                    </div>
                    <span className="text-[11px] font-bold" style={{ color: SKY }}>USD {tx.amt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Admin Tablet Mockup ─────────────────────────────────────────
function AdminTabletMockup() {
  return (
    <div className="mx-auto w-full max-w-sm">
      <div className="overflow-hidden rounded-[1.5rem] border-2 p-1" style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)" }}>
        <div className="overflow-hidden rounded-[1.2rem]" style={{ background: "#0a0f1a" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <p className="text-xs font-bold text-white">BRECCS Admin</p>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#10b981" }} />
              <span className="text-[9px]" style={{ color: "rgba(255,255,255,0.5)" }}>Synced</span>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-px p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            {[
              { label: "Learners", value: "842" },
              { label: "Meals today", value: "796" },
              { label: "Stock items", value: "34" },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                <p className="text-lg font-bold text-white">{kpi.value}</p>
                <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.4)" }}>{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* Pending approvals */}
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Pending approvals</p>
            <div className="space-y-2">
              {[
                { title: "Maize delivery — 50 bags", sub: "Supplier: GrainCo", status: "Verify", color: "#f59e0b" },
                { title: "Hardship credit — T. Moyo", sub: "USD 2.00 · Approved by Head", status: "Confirm", color: SKY },
                { title: "Stock adjustment — Cooking oil", sub: "-3 litres · Variance", status: "Review", color: "#ef4444" },
              ].map((item) => (
                <div key={item.title} className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-white">{item.title}</p>
                    <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>{item.sub}</p>
                  </div>
                  <span className="shrink-0 rounded-lg px-2.5 py-1 text-[9px] font-bold" style={{ background: `${item.color}20`, color: item.color }}>{item.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stock levels */}
          <div className="px-4 pb-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Stock levels</p>
            <div className="space-y-2">
              {[
                { name: "Maize meal", level: 62, color: "#10b981" },
                { name: "Cooking oil", level: 28, color: "#f59e0b" },
                { name: "Rice", level: 15, color: "#ef4444" },
              ].map((s) => (
                <div key={s.name}>
                  <div className="mb-1 flex justify-between">
                    <span className="text-[10px] font-medium text-white">{s.name}</span>
                    <span className="text-[10px]" style={{ color: s.color }}>{s.level}%</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${s.level}%` }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} className="h-full rounded-full" style={{ background: s.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Connectivity Dashboard Mockup ──────────────────────────────
function ConnectivityDashboardMockup() {
  return (
    <div className="mx-auto w-full max-w-lg">
      <div className="overflow-hidden rounded-2xl border-2 p-1" style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)" }}>
        <div className="overflow-hidden rounded-xl" style={{ background: "#0a0f1a" }}>
          {/* Browser bar */}
          <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: "rgba(255,255,255,0.04)" }}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#e0625a" }} />
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#e0a341" }} />
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#4bb554" }} />
            <span className="ml-2 text-[10px]" style={{ color: "rgba(255,255,255,0.4)" }}>breccs.co.zw / connectivity</span>
          </div>

          {/* Uptime gauge */}
          <div className="grid grid-cols-4 gap-px p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            {[
              { label: "Uptime", value: "99.2%", color: "#10b981" },
              { label: "Latency", value: "42ms", color: SKY },
              { label: "Devices", value: "24/26", color: "#f59e0b" },
              { label: "Cache hit", value: "87%", color: CYAN },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.4)" }}>{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* VLAN status */}
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Network segmentation</p>
            <div className="space-y-1.5">
              {[
                { name: "Admin VLAN", status: "Online", color: "#10b981" },
                { name: "Teacher VLAN", status: "Online", color: "#10b981" },
                { name: "Learner VLAN", status: "Online", color: "#10b981" },
                { name: "Guest VLAN", status: "Disabled", color: "#64748b" },
                { name: "Device VLAN", status: "Online", color: "#10b981" },
              ].map((vlan) => (
                <div key={vlan.name} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: vlan.color }} />
                    <span className="text-[11px] font-medium text-white">{vlan.name}</span>
                  </div>
                  <span className="text-[10px]" style={{ color: vlan.color }}>{vlan.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Outage log */}
          <div className="px-4 pb-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Recent outages</p>
            <div className="space-y-1.5">
              {[
                { dur: "14 min", cause: "Power failure — solar backup active", time: "Today 09:12" },
                { dur: "3 min", cause: "Carrier maintenance — LTE failover", time: "Yesterday 14:30" },
              ].map((o) => (
                <div key={o.time} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <div>
                    <p className="text-[10px] font-medium text-white">{o.cause}</p>
                    <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>{o.time}</p>
                  </div>
                  <span className="text-[10px] font-bold" style={{ color: "#f59e0b" }}>{o.dur}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edge Server Mockup ─────────────────────────────────────────
function EdgeServerMockup() {
  return (
    <div className="mx-auto w-full max-w-sm">
      <div className="overflow-hidden rounded-2xl border-2 p-1" style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.06)" }}>
        <div className="overflow-hidden rounded-xl" style={{ background: "#0a0f1a" }}>
          {/* Server header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="flex items-center gap-2">
              <PackageIcon size={16} style={{ color: SKY }} />
              <p className="text-xs font-bold text-white">Edge Server — Site #01</p>
            </div>
            <div className="flex items-center gap-1.5">
              <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }} className="h-2 w-2 rounded-full" style={{ background: "#10b981" }} />
              <span className="text-[9px]" style={{ color: "rgba(255,255,255,0.5)" }}>Running</span>
            </div>
          </div>

          {/* Resource gauges */}
          <div className="grid grid-cols-3 gap-px p-3" style={{ background: "rgba(255,255,255,0.04)" }}>
            {[
              { label: "CPU", value: 34, color: "#10b981" },
              { label: "Memory", value: 58, color: SKY },
              { label: "Storage", value: 41, color: CYAN },
            ].map((r) => (
              <div key={r.label} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.03)" }}>
                <div className="relative mx-auto h-16 w-16">
                  <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                    <motion.circle
                      cx="32" cy="32" r="26" fill="none" stroke={r.color} strokeWidth="5" strokeLinecap="round"
                      strokeDasharray={163.36}
                      initial={{ strokeDashoffset: 163.36 }}
                      whileInView={{ strokeDashoffset: 163.36 - (163.36 * r.value) / 100 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, ease: EASE }}
                    />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="text-sm font-bold text-white">{r.value}%</span>
                  </div>
                </div>
                <p className="mt-1 text-center text-[9px]" style={{ color: "rgba(255,255,255,0.4)" }}>{r.label}</p>
              </div>
            ))}
          </div>

          {/* Local services */}
          <div className="px-4 py-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>Local services</p>
            <div className="space-y-1.5">
              {[
                { name: "Canteen API", status: "Active", color: "#10b981" },
                { name: "Attendance sync", status: "Active", color: "#10b981" },
                { name: "Stock ledger", status: "Active", color: "#10b981" },
                { name: "Content cache", status: "87% cached", color: SKY },
                { name: "Procurement queue", status: "3 pending", color: "#f59e0b" },
              ].map((svc) => (
                <div key={svc.name} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <span className="text-[11px] font-medium text-white">{svc.name}</span>
                  <span className="text-[10px]" style={{ color: svc.color }}>{svc.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sync queue */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "rgba(2,132,199,0.1)", border: `1px solid ${SKY}30` }}>
              <div className="flex items-center gap-2">
                <motion.span animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="h-3 w-3 rounded-full border-2 border-t-transparent" style={{ borderColor: SKY }} />
                <span className="text-[11px] font-medium" style={{ color: SKY }}>Syncing — 47 queued transactions</span>
              </div>
              <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.4)" }}>Cloud</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ZIMBABWE — bento grid
// ════════════════════════════════════════════════════════════════
function ZimbabweSection() {
  return (
    <section className="relative py-24 sm:py-32" style={{ background: `linear-gradient(180deg, ${ICE} 0%, white 100%)` }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(224,242,254,0.6)", border: `1px solid ${LINE}` }}>
            <GlobeIcon size={14} style={{ color: OCEAN }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: OCEAN }}>Zimbabwe-native by design</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.03em" }}>
            Built for the operating realities of Zimbabwean schools.
          </h2>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ZIMBABWE_CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4, ease: EASE }}
                whileHover={{ y: -6 }}
                className="group rounded-3xl border p-5 backdrop-blur-md transition-all"
                style={{ borderColor: LINE, background: "rgba(255,255,255,0.6)" }}
              >
                <span className="grid h-12 w-12 place-items-center rounded-2xl text-white transition-transform group-hover:scale-110" style={{ background: `linear-gradient(135deg, ${SKY}, ${OCEAN})` }}>
                  <Icon size={24} />
                </span>
                <h3 className="mt-4 text-sm font-bold" style={{ color: INK }}>{c.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed" style={{ color: "#0369A1" }}>{c.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// BUYER ROLES
// ════════════════════════════════════════════════════════════════
function BuyerSection() {
  return (
    <section className="relative py-24 sm:py-32" style={{ background: "white" }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(224,242,254,0.6)", border: `1px solid ${LINE}` }}>
            <UsersIcon size={14} style={{ color: OCEAN }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: OCEAN }}>Built for the people who carry the risk</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.03em" }}>Every role gets the control they need.</h2>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BUYER_ROLES.map((r, i) => {
            const Icon = r.icon;
            return (
              <motion.div
                key={r.role}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4, ease: EASE }}
                whileHover={{ y: -6 }}
                className="group rounded-3xl border p-5 backdrop-blur-md transition-all"
                style={{ borderColor: LINE, background: "rgba(240,249,255,0.6)" }}
              >
                <span className="grid h-12 w-12 place-items-center rounded-2xl text-white transition-transform group-hover:scale-110" style={{ background: `linear-gradient(135deg, ${SKY}, ${OCEAN})` }}>
                  <Icon size={24} />
                </span>
                <h3 className="mt-4 text-sm font-bold" style={{ color: OCEAN }}>{r.role}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#0369A1" }}>{r.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// CTA — dark immersive with gradient
// ════════════════════════════════════════════════════════════════
function CTASection() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="audit" className="relative py-24 sm:py-36 overflow-hidden" style={{ background: `linear-gradient(180deg, ${OCEAN} 0%, ${INK} 100%)`, color: "white" }}>
      <GradientMesh variant="dark" />

      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <SparklesIcon size={14} style={{ color: SKY }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: SKY }}>Get started</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ letterSpacing: "-0.035em" }}>
            Give your school a resilient network,<br />cashless learners, and a trusted financial record.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.65 }}>
            Book a 30-minute demo. See how LearnerPass, School Commerce, managed connectivity, and supplier finance control connect — and where your school's money is leaking today.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row justify-center">
            <Button size="lg" onClick={() => setOpen(true)} style={{ background: `linear-gradient(135deg, ${SKY}, ${CYAN})`, color: INK, fontWeight: 700, border: "none" }} className="hover:scale-105 transition-transform">
              Book a demo <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <a href="#school-in-a-box">
              <Button size="lg" variant="outline" style={{ borderColor: "rgba(255,255,255,0.3)", color: "white", background: "rgba(255,255,255,0.05)", backdropFilter: "blur(10px)" }} className="hover:scale-105 transition-transform">
                See School-in-a-Box
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Data stays in Zimbabwe</span>
            <span className="flex items-center gap-1.5"><ShieldCheckIcon size={14} /> Cyber & Data Protection Act ready</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> No rip-and-replace</span>
          </div>
        </motion.div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSubmitted(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Book a demo</DialogTitle>
            <DialogDescription>We will contact you within 24 hours to schedule a 30-minute walkthrough.</DialogDescription>
          </DialogHeader>
          {submitted ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12" style={{ color: OCEAN }} />
              <p className="mt-4 text-sm font-medium" style={{ color: INK }}>Thank you. Your demo request has been received — we will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor="name">Name</Label><Input id="name" required /></div>
                <div><Label htmlFor="org">School / Group</Label><Input id="org" required /></div>
                <div><Label htmlFor="role">Role</Label><Input id="role" /></div>
                <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required /></div>
                <div><Label htmlFor="phone">Phone</Label><Input id="phone" /></div>
                <div><Label htmlFor="schools">Number of schools</Label><Input id="schools" type="number" min={1} /></div>
              </div>
              <DialogFooter>
                <Button type="submit" style={{ background: `linear-gradient(135deg, ${OCEAN}, ${SKY})`, color: "white", border: "none" }}>Request demo</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
